import { Flex, LabelVariant, Label, Text, Tooltip } from '@workwhile/ui'
import { UserPaymentStatus } from 'api/payments'
import {
  HighnoteProvider,
  HighnoteUserPayment,
  isPayoutPayment,
} from 'api/payments/getUserPayments'
import { usePayoutSummary } from 'queries/payments/usePayoutSummary'
import { toTitleCase } from 'lib/string'
import React from 'react'
import { ProviderDetailLine } from './ProviderDetailLine'

export const PaymentStatusRender = ({
  value,
  highnote,
}: {
  value: UserPaymentStatus
  highnote?: HighnoteProvider | null
}) => {
  const getStatusVariant = (status: UserPaymentStatus): LabelVariant => {
    switch (status) {
      case UserPaymentStatus.COMPLETED:
        return 'success'
      case UserPaymentStatus.PENDING:
        return 'information'
      case UserPaymentStatus.FAILED:
      case UserPaymentStatus.CANCELED:
      case UserPaymentStatus.REVERSED:
        return 'error'
      default:
        return 'neutral'
    }
  }

  const variant = getStatusVariant(value)

  const statusContent = (
    <Flex flexDirection="column" gap={0.5} alignItems="flex-start">
      <Label variant={variant} size="small">
        {toTitleCase(value.toLowerCase())}
      </Label>
    </Flex>
  )

  if (!highnote?.failureReason) {
    return statusContent
  }

  return (
    <Tooltip
      content={
        <Flex flexDirection="column" gap={0.5}>
          {highnote.transferStatus ? (
            <ProviderDetailLine
              label="Transfer status"
              value={toTitleCase(highnote.transferStatus)}
            />
          ) : null}
          {highnote.failureReason ? (
            <Text fontSize="sm" color="error">
              Failure:{' '}
              {highnote.failureReasonDescription ??
                toTitleCase(highnote.failureReason)}
            </Text>
          ) : null}
        </Flex>
      }
    >
      {statusContent}
    </Tooltip>
  )
}

export const HighnotePaymentStatusRender = ({
  payment,
}: {
  payment: HighnoteUserPayment
}) => {
  const { data: payoutSummary } = usePayoutSummary(
    isPayoutPayment(payment.eventType) ? payment.id : null
  )

  return (
    <PaymentStatusRender
      value={payment.status}
      highnote={payoutSummary?.highnote}
    />
  )
}
